import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCloud, FiShield, FiZap, FiLock, FiMail, FiEye, FiMonitor } = FiIcons;

const InfrastructureStack = () => {
  return (
    <section id="infrastructure" className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Infrastructure & Security Stack
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Built on enterprise-grade platforms trusted by the world's largest organizations.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Cloudflare Stack */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                <SafeIcon icon={FiCloud} className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900">Cloudflare</h3>
                <p className="text-slate-600">Global Edge Network</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <SafeIcon icon={FiZap} className="w-5 h-5 text-emerald-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900">DNS Management</h4>
                  <p className="text-slate-600 text-sm">Lightning-fast global DNS resolution</p>
                </div>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiShield} className="w-5 h-5 text-emerald-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900">Web Application Firewall</h4>
                  <p className="text-slate-600 text-sm">Advanced threat protection and filtering</p>
                </div>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiLock} className="w-5 h-5 text-emerald-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900">SSL/TLS Configuration</h4>
                  <p className="text-slate-600 text-sm">Enterprise-grade encryption standards</p>
                </div>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiShield} className="w-5 h-5 text-emerald-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900">DDoS Mitigation</h4>
                  <p className="text-slate-600 text-sm">Automatic attack detection and blocking</p>
                </div>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiCloud} className="w-5 h-5 text-emerald-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900">Scalable Domain Management</h4>
                  <p className="text-slate-600 text-sm">Manage unlimited domains with ease</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Xcitium Stack */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900">Xcitium</h3>
                <p className="text-slate-600">Cybersecurity & Email Protection</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <SafeIcon icon={FiMail} className="w-5 h-5 text-emerald-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900">Email Security</h4>
                  <p className="text-slate-600 text-sm">Advanced SPAM filtering and threat detection</p>
                </div>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiMonitor} className="w-5 h-5 text-emerald-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900">Endpoint Protection</h4>
                  <p className="text-slate-600 text-sm">Comprehensive endpoint security solutions</p>
                </div>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiEye} className="w-5 h-5 text-emerald-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900">Advanced Threat Detection</h4>
                  <p className="text-slate-600 text-sm">Real-time threat analysis and response</p>
                </div>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiLock} className="w-5 h-5 text-emerald-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900">Zero-Day Protection</h4>
                  <p className="text-slate-600 text-sm">Proactive defense against unknown threats</p>
                </div>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiShield} className="w-5 h-5 text-emerald-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900">Network Security</h4>
                  <p className="text-slate-600 text-sm">Enterprise-grade network protection</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 mt-6">
              <h4 className="font-semibold text-slate-900 mb-2">Complete Cybersecurity Suite</h4>
              <p className="text-slate-600 text-sm">
                As an Xcitium partner, we provide comprehensive cybersecurity solutions including 
                endpoint protection, network security, and advanced threat intelligence.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default InfrastructureStack;